/**
 * 10.9GQ — TEXTURAS PROCEDURAIS DO CHÃO (albedo + normal map), 100% geradas em código.
 *
 * Antes o chão era UMA textura cinza de manchas aleatórias (não repetível: costura visível) sem
 * relevo. Aqui cada ESTILO ganha um campo de altura repetível (ruído de valor periódico + células
 * de Voronoi periódicas), do qual saem:
 *   · albedo em tons de cinza (multiplica a cor de bioma pintada nos vértices) com leve tinta;
 *   · normal map (Sobel do campo de altura) — o sol passa a "pegar" nas placas, fendas e tufos.
 *
 * Tudo é cacheado por estilo (gera uma vez por sessão). Custo: ~40–80 ms por estilo em 512².
 */
import * as THREE from "three";
import { quality } from "../core/Quality";

export type GroundStyle = "grass" | "rock" | "storm" | "ice" | "ash";

export interface GroundTextureSet {
  map: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
}

// 10.9IE — era 512 fixo. Gerar 5 estilos × (albedo + normal) custava 40-80 ms CADA no desktop:
// medido, a 1ª entrada numa região travava 301 ms aqui (no celular fraco, segundos). Em 256 a
// área cai 4×, e no preset baixo o normal map nem é usado. O cache por estilo continua igual.
const size = (): number => (quality.values.groundTexSize ?? 512);
const cache = new Map<GroundStyle, GroundTextureSet>();

// ------------------------------------------------------------------ ruído periódico
function hashP(ix: number, iy: number, period: number, periodY = period): number {
  const x = ((ix % period) + period) % period;
  const y = ((iy % periodY) + periodY) % periodY;
  const s = Math.sin(x * 127.1 + y * 311.7 + 74.7) * 43758.5453;
  return s - Math.floor(s);
}
/** Ruído de valor 2D com período inteiro (repete sem costura). x,y em unidades de lattice. */
function pnoise(x: number, y: number, period: number, periodY = period): number {
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const fx = x - x0, fy = y - y0;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const a = hashP(x0, y0, period, periodY), b = hashP(x0 + 1, y0, period, periodY);
  const c = hashP(x0, y0 + 1, period, periodY), d = hashP(x0 + 1, y0 + 1, period, periodY);
  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}
/** fBm periódico: u,v em [0,1). `period` = células da 1ª oitava. */
function fbm(u: number, v: number, period: number, oct = 4, gain = 0.5): number {
  let amp = 1, sum = 0, norm = 0, p = period;
  for (let i = 0; i < oct; i++) {
    sum += pnoise(u * p, v * p, p) * amp;
    norm += amp;
    amp *= gain;
    p *= 2;
  }
  return sum / norm;
}
/** Voronoi periódico (distância ao ponto mais próximo e ao 2º). u,v em [0,1); `cells` por lado. */
function voronoi(u: number, v: number, cells: number, seed = 0): { d1: number; d2: number; id: number } {
  const x = u * cells, y = v * cells;
  const ix = Math.floor(x), iy = Math.floor(y);
  let d1 = 1e9, d2 = 1e9, id = 0;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const cx = ix + ox, cy = iy + oy;
      const px = cx + hashP(cx + seed, cy, cells);
      const py = cy + hashP(cx, cy + seed + 7, cells);
      const dx = px - x, dy = py - y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < d1) { d2 = d1; d1 = d; id = hashP(cx + 3 + seed, cy + 11, cells) * 1000 | 0; } else if (d < d2) d2 = d;
    }
  }
  return { d1, d2, id };
}
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (a: number, b: number, x: number): number => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };

// ------------------------------------------------------------------ estilos
interface Sample { h: number; r: number; g: number; b: number }
type StyleFn = (u: number, v: number, out: Sample) => void;

const STYLES: Record<GroundStyle, { fn: StyleFn; normalStrength: number }> = {
  // relva: montículos suaves + tufos miúdos + terra batida nas baixadas
  grass: {
    normalStrength: 1.6,
    fn: (u, v, o) => {
      const big = fbm(u, v, 3, 3);
      const clump = fbm(u + 0.31, v + 0.17, 9, 3, 0.5);
      // traços alongados (folhas deitadas) em duas direções — sem ruído fino de 1 oitava (virava labirinto)
      const blade = pnoise(u * 28, v * 140, 28, 140) * 0.6 + pnoise(u * 120 + 3, v * 24 + 7, 120, 24) * 0.4;
      // tufos: pontos escuros esparsos (moitas vistas de cima), o que mais "lê" como grama no top-down
      // (agrupados por uma máscara de ruído e com tamanho variável — senão vira "bolinha" regular)
      const tv = voronoi(u, v, 34, 9);
      const tuftMask = smooth(0.42, 0.66, fbm(u + 0.7, v + 0.2, 5, 2));
      const tuftR = 0.16 + (tv.id % 5) * 0.05;
      const tuft = (1 - smooth(tuftR * 0.4, tuftR + 0.1, tv.d1)) * tuftMask;
      const h = big * 0.42 + clump * 0.38 + blade * 0.08 + tuft * 0.12;
      const soil = smooth(0.62, 0.78, big); // clareira de terra
      let g = 0.74 + (h - 0.5) * 0.4 - tuft * 0.11;
      g *= 1 - soil * 0.22;
      o.h = h;
      o.r = g * (1 + soil * 0.16); o.g = g * (1 + (1 - soil) * 0.04); o.b = g * (1 - soil * 0.12);
    },
  },
  // placas de rocha rachadas (Voronoi) com granulado
  rock: {
    normalStrength: 2.6,
    fn: (u, v, o) => {
      const vo = voronoi(u + fbm(u, v, 5, 2) * 0.03, v, 7, 1);
      const edge = vo.d2 - vo.d1;
      const crack = smooth(0.0, 0.11, edge); // 0 na fenda, 1 no meio da placa
      const plate = 0.35 + (vo.id % 7) / 7 * 0.3; // cada placa com altura própria
      const grain = fbm(u, v, 22, 3, 0.5);
      const h = crack * (plate + grain * 0.25) + (1 - crack) * 0.05;
      const g = 0.5 + h * 0.5 - (1 - crack) * 0.28;
      o.h = h;
      o.r = g * 1.02; o.g = g; o.b = g * 0.97;
    },
  },
  // pedra estriada da Tempestade: camadas onduladas + musgo nas frestas
  storm: {
    normalStrength: 2.0,
    fn: (u, v, o) => {
      const warp = fbm(u, v, 4, 3) * 0.35;
      const band = 0.5 + 0.5 * Math.sin((v * 5 + warp) * Math.PI * 2);
      const grain = fbm(u + 0.5, v, 14, 4);
      const h = band * 0.32 + grain * 0.68;
      const moss = smooth(0.58, 0.8, fbm(u * 1.7, v * 1.7, 6, 2)) * (1 - band);
      const g = 0.52 + h * 0.42;
      o.h = h;
      o.r = g * (1 - moss * 0.3); o.g = g * (1 + moss * 0.05); o.b = g * (1 - moss * 0.28);
    },
  },
  // gelo: superfície lisa com fendas finas claras e bolhas
  ice: {
    normalStrength: 1.2,
    fn: (u, v, o) => {
      const vo = voronoi(u, v + fbm(u, v, 6, 2) * 0.04, 5, 3);
      const edge = vo.d2 - vo.d1;
      const crack = 1 - smooth(0.0, 0.035, edge);
      const smoothH = fbm(u, v, 3, 3, 0.45);
      const h = smoothH * 0.8 - crack * 0.35;
      const g = 0.86 + smoothH * 0.1 + crack * 0.12;
      o.h = h;
      o.r = g * 0.93; o.g = g * 0.98; o.b = Math.min(1, g * 1.05);
    },
  },
  // cinza vulcânica: placas escuras rachadas com brasa nas fendas
  ash: {
    normalStrength: 2.4,
    fn: (u, v, o) => {
      const vo = voronoi(u, v, 9, 5);
      const edge = vo.d2 - vo.d1;
      const crack = smooth(0.0, 0.07, edge);
      const grain = fbm(u, v, 20, 3);
      const h = crack * (0.45 + grain * 0.3) + (1 - crack) * 0.08;
      const ember = (1 - crack) * smooth(0.35, 0.7, fbm(u + 0.2, v + 0.6, 4, 2));
      const g = 0.4 + h * 0.35;
      o.h = h;
      o.r = g + ember * 0.75; o.g = g * 0.96 + ember * 0.28; o.b = g * 0.94 + ember * 0.05;
    },
  },
};

// ------------------------------------------------------------------ geração
function build(style: GroundStyle): GroundTextureSet {
  const S = size();
  const st = STYLES[style];
  const H = new Float32Array(S * S);
  const img = new Uint8ClampedArray(new ArrayBuffer(S * S * 4));
  const o: Sample = { h: 0, r: 0, g: 0, b: 0 };
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      st.fn(x / S, y / S, o);
      const i = y * S + x;
      H[i] = o.h;
      img[i * 4] = clamp01(o.r) * 255;
      img[i * 4 + 1] = clamp01(o.g) * 255;
      img[i * 4 + 2] = clamp01(o.b) * 255;
      img[i * 4 + 3] = 255;
    }
  }
  // normal map por Sobel (bordas com wrap → continua repetível)
  const nrm = new Uint8ClampedArray(new ArrayBuffer(S * S * 4));
  const k = st.normalStrength * S * 0.02;
  for (let y = 0; y < S; y++) {
    const ym = (y - 1 + S) % S, yp = (y + 1) % S;
    for (let x = 0; x < S; x++) {
      const xm = (x - 1 + S) % S, xp = (x + 1) % S;
      const tl = H[ym * S + xm], t = H[ym * S + x], tr = H[ym * S + xp];
      const l = H[y * S + xm], r = H[y * S + xp];
      const bl = H[yp * S + xm], b = H[yp * S + x], br = H[yp * S + xp];
      const dx = (tr + 2 * r + br) - (tl + 2 * l + bl);
      const dy = (bl + 2 * b + br) - (tl + 2 * t + tr);
      let nx = -dx * k, ny = dy * k, nz = 1;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx /= len; ny /= len; nz /= len;
      const i = (y * S + x) * 4;
      nrm[i] = (nx * 0.5 + 0.5) * 255;
      nrm[i + 1] = (ny * 0.5 + 0.5) * 255;
      nrm[i + 2] = (nz * 0.5 + 0.5) * 255;
      nrm[i + 3] = 255;
    }
  }
  const toTex = (data: Uint8ClampedArray<ArrayBuffer>, srgb: boolean): THREE.CanvasTexture => {
    const c = document.createElement("canvas");
    c.width = c.height = S;
    const ctx = c.getContext("2d")!;
    ctx.putImageData(new ImageData(data, S, S), 0, 0);
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 8;
    if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  };
  return { map: toTex(img, true), normalMap: toTex(nrm, false) };
}

/** Conjunto (albedo + normal) de um estilo — gerado na 1ª vez, depois cacheado (nunca descartar). */
export function groundTextures(style: GroundStyle): GroundTextureSet {
  let set = cache.get(style);
  if (!set) { set = build(style); cache.set(style, set); }
  return set;
}

// ------------------------------------------------------------------ decals
export type DecalKind = "crack" | "moss" | "rune";
const decalCache = new Map<DecalKind, THREE.CanvasTexture>();

/** Textura RGBA de um decal (rachadura / musgo / runa) — branca, tingida pela cor da instância. */
export function decalTexture(kind: DecalKind): THREE.CanvasTexture {
  const hit = decalCache.get(kind);
  if (hit) return hit;
  const S = 128;
  const c = document.createElement("canvas");
  c.width = c.height = S;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, S, S);
  const rnd = (a: number, b: number): number => a + Math.random() * (b - a);
  if (kind === "crack") {
    ctx.strokeStyle = "rgba(255,255,255,1)";
    ctx.lineCap = "round";
    const branch = (x: number, y: number, ang: number, len: number, w: number, depth: number): void => {
      if (depth > 4 || len < 6 || w < 0.6) return;
      const nx = x + Math.cos(ang) * len, ny = y + Math.sin(ang) * len;
      ctx.lineWidth = w;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(nx, ny); ctx.stroke();
      branch(nx, ny, ang + rnd(-0.5, 0.5), len * rnd(0.6, 0.9), w * 0.75, depth + 1);
      if (Math.random() < 0.6) branch(nx, ny, ang + rnd(0.6, 1.4) * (Math.random() < 0.5 ? 1 : -1), len * rnd(0.4, 0.7), w * 0.6, depth + 1);
    };
    for (let i = 0; i < 3; i++) branch(S / 2 + rnd(-8, 8), S / 2 + rnd(-8, 8), rnd(0, Math.PI * 2), rnd(18, 30), rnd(2.2, 3.4), 0);
  } else if (kind === "moss") {
    for (let i = 0; i < 26; i++) {
      const x = S / 2 + rnd(-30, 30), y = S / 2 + rnd(-30, 30), r = rnd(8, 22);
      const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, "rgba(255,255,255,0.95)");
      grad.addColorStop(0.7, "rgba(255,255,255,0.55)");
      grad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    }
  } else {
    ctx.strokeStyle = "rgba(255,255,255,1)";
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(S / 2, S / 2, 52, 0, Math.PI * 2); ctx.stroke();
    ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.arc(S / 2, S / 2, 40, 0, Math.PI * 2); ctx.stroke();
    // glifos: traços radiais curtos entre os dois anéis + um símbolo central
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const r0 = 42, r1 = 50 - (i % 3) * 3;
      ctx.lineWidth = i % 2 ? 2.4 : 1.4;
      ctx.beginPath(); ctx.moveTo(S / 2 + Math.cos(a) * r0, S / 2 + Math.sin(a) * r0); ctx.lineTo(S / 2 + Math.cos(a) * r1, S / 2 + Math.sin(a) * r1); ctx.stroke();
    }
    ctx.lineWidth = 2.2;
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
      ctx.beginPath(); ctx.moveTo(S / 2, S / 2); ctx.lineTo(S / 2 + Math.cos(a) * 26, S / 2 + Math.sin(a) * 26); ctx.stroke();
    }
    ctx.beginPath(); ctx.arc(S / 2, S / 2, 7, 0, Math.PI * 2); ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  tex.colorSpace = THREE.SRGBColorSpace;
  decalCache.set(kind, tex);
  return tex;
}
